import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ProposalExplorer from './ProposalExplorer';
import './css/CosmosStats.css';

const ranges = [
  { days: 1, label: '24h' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 365, label: '1y' },
];

const compact = (value, options = {}) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '--';
  return Number(value).toLocaleString('en-GB', { notation: 'compact', maximumFractionDigits: 2, ...options });
};

const full = (value, digits = 2) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '--';
  return Number(value).toLocaleString('en-GB', { maximumFractionDigits: digits });
};

const usd = (value, digits = 2) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '--';
  return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: digits });
};

const percent = (value, isRatio = true) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '--';
  return `${(Number(value) * (isRatio ? 100 : 1)).toFixed(2)}%`;
};

const hasNumber = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

const CosmosStats = () => {
  const { chainKey } = useParams();
  const location = useLocation();
  const proposalId = new URLSearchParams(location.search).get('proposal');
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [rangeDays, setRangeDays] = useState(7);
  const [view, setView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [proposalsOpen, setProposalsOpen] = useState(false);

  useEffect(() => {
    axios.get('/api/cosmos/chains').then((response) => {
      const chains = response.data?.chains || [];
      setCatalog(chains);
      if (!chainKey && chains.length) navigate(`/stats/${chains.some((chain) => chain.key === 'terra') ? 'terra' : chains[0].key}`, { replace: true });
    }).catch(() => setError('The Cosmos chain catalogue is temporarily unavailable.'));
  }, [chainKey, navigate]);

  useEffect(() => {
    if (!chainKey) return;
    let active = true;
    setLoading(true);
    setError('');
    setStats(null);
    setHistory([]);

    axios.get(`/api/cosmos/chains/${encodeURIComponent(chainKey)}/stats`).then(({ data }) => {
      if (active) setStats(data);
    }).catch((failure) => {
      if (active) setError(failure.response?.data?.message || 'Live chain statistics are unavailable.');
    }).finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [chainKey]);

  useEffect(() => {
    const catalogChain = catalog.find((item) => item.key === chainKey);
    if (!chainKey || !catalogChain?.coingeckoId) return undefined;
    let active = true;
    setHistory([]);
    axios.get(`/api/cosmos/chains/${encodeURIComponent(chainKey)}/price-history`, { params: { days: rangeDays } })
      .then(({ data }) => { if (active) setHistory(data?.prices || []); })
      .catch(() => { if (active) setHistory([]); });
    return () => { active = false; };
  }, [catalog, chainKey, rangeDays]);

  useEffect(() => { setView('overview'); setSearch(''); setProposalsOpen(Boolean(proposalId)); }, [chainKey, proposalId]);

  const filteredChains = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return catalog;
    return catalog.filter((chain) => `${chain.prettyName} ${chain.chainId} ${chain.symbol}`.toLowerCase().includes(query));
  }, [catalog, search]);

  const sampledHistory = useMemo(() => {
    if (history.length <= 120) return history;
    const step = Math.ceil(history.length / 120);
    return history.filter((_, index) => index % step === 0 || index === history.length - 1);
  }, [history]);
  const historyValues = sampledHistory.map((point) => Number(point[1])).filter(Number.isFinite);
  const historyMin = historyValues.length ? Math.min(...historyValues) : 0;
  const historyMax = historyValues.length ? Math.max(...historyValues) : 1;
  const historySpan = Math.max(historyMax - historyMin, Number.EPSILON);

  const selectedCatalogChain = catalog.find((chain) => chain.key === chainKey);
  const chain = stats?.chain || selectedCatalogChain;
  const move = stats?.market?.change24h;
  const supplySymbol = chain?.symbol || '';
  const hasMarketSnapshot = stats && [stats.market?.priceUsd, stats.market?.marketCapUsd, stats.market?.volume24hUsd].some(hasNumber);
  const marketMetrics = stats && (hasMarketSnapshot || history.length > 0);
  const networkMetrics = stats && [stats.network?.height, stats.validators?.bonded, stats.validators?.total].some(hasNumber);
  const stakingMetrics = stats && [stats.staking?.bonded, stats.staking?.notBonded, stats.staking?.ratio, stats.supply?.total, stats.inflation, stats.communityPool].some(hasNumber);
  const governanceMetrics = stats && (hasNumber(stats.governance?.openProposals) || Boolean(proposalId));
  const views = [
    { key: 'overview', label: 'All available stats', available: true },
    { key: 'market', label: 'Market', available: marketMetrics },
    { key: 'network', label: 'Network', available: networkMetrics },
    { key: 'staking', label: 'Staking & supply', available: stakingMetrics },
    { key: 'governance', label: 'Governance', available: governanceMetrics },
  ].filter((item) => item.available);
  const show = (category) => view === 'overview' || view === category;

  return (
    <main className="cosmos-stats-page">
      <section className="cosmos-stats-toolbar">
        <div className="cosmos-chain-heading">
          {chain?.logo && <img src={chain.logo} alt="" />}
          <div>
            <p>Cosmos mainnet intelligence</p>
            <h1>{chain?.prettyName || 'Multi-chain Stats'}</h1>
            <span>{chain?.chainId || 'Choose a live network'}</span>
          </div>
        </div>
        <div className="cosmos-chain-picker">
          <label htmlFor="cosmos-chain-search">Find a chain</label>
          <input id="cosmos-chain-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, chain ID or symbol" />
          <select value={chainKey || ''} onChange={(event) => navigate(`/stats/${event.target.value}`)} aria-label="Select Cosmos chain">
            <option value="" disabled>Choose a network</option>
            {filteredChains.map((item) => <option key={item.key} value={item.key}>{item.prettyName} · {item.symbol}</option>)}
          </select>
        </div>
      </section>

      {error && <p className="cosmos-stats-error" role="alert">{error}</p>}
      {loading && <p className="cosmos-stats-loading">Loading live chain data...</p>}

      {stats && (
        <>
          <div className="cosmos-dashboard-intro">
            <div><p>Chain dashboard</p><h2>{chain.prettyName} at a glance</h2><span>Live data for {chain.chainId}. Figures appear only when this network reports them.</span></div>
            <div className="cosmos-dashboard-actions">
              {selectedCatalogChain?.validatorsAvailable && <Link className="cosmos-view-validators" to={`/Services?chain=${encodeURIComponent(chainKey)}`}>View {chain.prettyName} validators <span aria-hidden="true">→</span></Link>}
            <label htmlFor="cosmos-stat-view">Show statistics
              <select id="cosmos-stat-view" value={view} onChange={(event) => setView(event.target.value)}>
                {views.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
              </select>
            </label>
            </div>
          </div>

          {hasMarketSnapshot && show('market') && <section className="cosmos-market-band" aria-label="Market statistics">
            {hasNumber(stats.market?.priceUsd) && <div>
              <span>Live price</span>
              <strong>{usd(stats.market?.priceUsd, stats.market?.priceUsd < 1 ? 8 : 2)}</strong>
              {hasNumber(move) && <small className={move >= 0 ? 'positive' : 'negative'}>{move >= 0 ? '+' : ''}{move.toFixed(2)}% over 24 hours</small>}
            </div>}
            {hasNumber(stats.market?.marketCapUsd) && <div><span>Market cap</span><strong>{usd(stats.market.marketCapUsd, 0)}</strong></div>}
            {hasNumber(stats.market?.volume24hUsd) && <div><span>24h off-chain volume</span><strong>{usd(stats.market.volume24hUsd, 0)}</strong></div>}
          </section>}

          {marketMetrics && show('market') && sampledHistory.length > 0 && <section className="cosmos-chart-section">
            <div className="cosmos-section-heading">
              <div><p>Market history</p><h2>{chain.prettyName} price</h2></div>
              <div className="cosmos-range-control" aria-label="Chart range">
                {ranges.map((range) => <button key={range.days} type="button" className={rangeDays === range.days ? 'active' : ''} onClick={() => setRangeDays(range.days)}>{range.label}</button>)}
              </div>
            </div>
            <div className="cosmos-price-bars" aria-label={`${chain.prettyName} price history`}>
                {sampledHistory.map(([timestamp, price], index) => {
                  const date = new Date(timestamp);
                  const height = 10 + (((Number(price) - historyMin) / historySpan) * 90);
                  const priceLabel = usd(price, Number(price) < 0.0001 ? 12 : Number(price) < 1 ? 8 : 2);
                  const dateLabel = date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
                  const edge = index < 8 ? 'cosmos-price-bar-start' : index >= sampledHistory.length - 8 ? 'cosmos-price-bar-end' : '';
                  return <div key={`${timestamp}-${index}`} className={`cosmos-price-bar ${edge}`} style={{ height: `${height}%` }} aria-label={`${dateLabel}: ${priceLabel}`}>
                    <div className="cosmos-price-tooltip" aria-hidden="true">
                      <span className="cosmos-price-tooltip-label">{chain.symbol} price</span>
                      <strong>{priceLabel}</strong>
                      <time dateTime={date.toISOString()}>{dateLabel}</time>
                    </div>
                  </div>;
                })}
            </div>
            <div className="cosmos-chart-scale"><span>{usd(historyMin, historyMin < 1 ? 8 : 2)}</span><span>{usd(historyMax, historyMax < 1 ? 8 : 2)}</span></div>
          </section>}

          {networkMetrics && show('network') && <section className="cosmos-network-section">
            <div className="cosmos-section-heading"><div><p>On-chain status</p><h2>Network</h2></div><span>Updated {new Date(stats.updatedAt).toLocaleTimeString('en-GB')}</span></div>
            <div className="cosmos-metric-grid">
              {hasNumber(stats.network?.height) && <article><span>Latest block</span><strong>{full(stats.network.height, 0)}</strong>{stats.network.blockTime && <small>{new Date(stats.network.blockTime).toLocaleString('en-GB')}</small>}</article>}
              {hasNumber(stats.validators?.bonded) && <article><span>Bonded validators</span><strong>{full(stats.validators.bonded, 0)}</strong>{hasNumber(stats.validators?.total) && <small>{full(stats.validators.total, 0)} total registered</small>}<Link className="cosmos-all-validators" to={`/Services?chain=${encodeURIComponent(chainKey)}&status=all`}>View all validators →</Link></article>}
              {!hasNumber(stats.validators?.bonded) && hasNumber(stats.validators?.total) && <article><span>Registered validators</span><strong>{full(stats.validators.total, 0)}</strong><Link className="cosmos-all-validators" to={`/Services?chain=${encodeURIComponent(chainKey)}&status=all`}>View all validators →</Link></article>}
            </div>
          </section>}

          {stakingMetrics && show('staking') && <section className="cosmos-network-section">
            <div className="cosmos-section-heading"><div><p>On-chain economics</p><h2>Staking & supply</h2></div></div>
            <div className="cosmos-metric-grid">
              {hasNumber(stats.staking?.bonded) && <article><span>Bonded stake</span><strong>{compact(stats.staking.bonded)} {supplySymbol}</strong>{hasNumber(stats.staking?.ratio) && <small>{percent(stats.staking.ratio)} of supply</small>}</article>}
              {hasNumber(stats.staking?.notBonded) && <article><span>Unbonded stake</span><strong>{compact(stats.staking.notBonded)} {supplySymbol}</strong></article>}
              {hasNumber(stats.supply?.total) && <article><span>Total supply</span><strong>{compact(stats.supply.total)} {supplySymbol}</strong><small>{full(stats.supply.total, 2)}</small></article>}
              {hasNumber(stats.inflation) && <article><span>Inflation</span><strong>{percent(stats.inflation)}</strong><small>Reported by the mint module</small></article>}
              {hasNumber(stats.communityPool) && <article><span>Community pool</span><strong>{compact(stats.communityPool)} {supplySymbol}</strong></article>}
            </div>
          </section>}

          {governanceMetrics && show('governance') && <section className="cosmos-network-section">
            <div className="cosmos-section-heading"><div><p>Governance</p><h2>Current proposals</h2></div></div>
            <div className="cosmos-metric-grid"><button type="button" className="cosmos-proposal-trigger" aria-expanded={proposalsOpen} aria-controls="cosmos-proposal-explorer" onClick={() => setProposalsOpen((open) => !open)}><span>Open proposals</span><strong>{full(stats.governance.openProposals, 0)}</strong><small>{proposalsOpen ? 'Hide proposals' : 'View proposals and votes →'}</small></button></div>
            {proposalsOpen && <ProposalExplorer key={chainKey} chainKey={chainKey} chainName={chain.prettyName} initialId={proposalId} />}
          </section>}

          {!marketMetrics && !networkMetrics && !stakingMetrics && !governanceMetrics && <p className="cosmos-stats-loading" role="status">This network has no reportable statistics right now. Try again later.</p>}

          <section className="cosmos-setup-band">
            <div>
              <p>Validator deployment</p>
              <h2>{chain.setupStatus === 'automated' ? 'Automated setup available' : 'Provisioning profile under verification'}</h2>
              <span>{chain.setupStatus === 'automated' ? 'Assigned client accounts can use the validator setup workflow.' : 'Stats are live. Automated server access and transaction signing stay disabled until this chain profile has passed installation and recovery tests.'}</span>
            </div>
            <div className="cosmos-setup-actions">
              {chain.setupStatus === 'automated' && <Link to="/validatorinfo">Validator setup</Link>}
              {chain.key === 'terra' && <Link to="/classic-dashboard">Detailed LUNC dashboard</Link>}
              {(chain.key === 'terra' || chain.website) && <a href={chain.key === 'terra' ? 'https://statsbin.com/' : chain.website} target="_blank" rel="noopener noreferrer">{chain.key === 'terra' ? 'Stats Bin' : 'Official website'}</a>}
              {chain.apiDocs && <a href={chain.apiDocs} target="_blank" rel="noopener noreferrer">API docs</a>}
            </div>
          </section>
        </>
      )}
    </main>
  );
};

export default CosmosStats;
